/**
 * Аялал - Орос — НИЙЛҮҮЛЭЛТИЙН БҮРТГЭЛ.
 *
 * Энэ хуудас нэг л асуултад хариулна: **хамгийн сүүлд ямар аялал ирсэн
 * бэ.** Аялал бүр НЭГ МӨР, шинэ нь дээрээ; дарвал `/purchasing/:id`
 * дэлгэрэнгүй хуудас руу орно.
 *
 * ⚠️ Өмнө нь нийлүүлэгч бүр таб байж, доор нь аялал бүрийн задаргаа
 *    ЗЭРЭГ задарсан байв. Тэр нь хоёр асуултыг нэг дэлгэцэд хольсон:
 *    «сүүлд юу ирсэн» (он цагийн) ба «энэ нийлүүлэгч юу нийлүүлдэг»
 *    (нийлүүлэгчийн). Эхнийх нь өдөр тутмынх тул хуудсыг тэр эзэлнэ;
 *    нийлүүлэгч нь ШҮҮЛТҮҮР болж үлдэв.
 *
 * ⚠️ Нийлүүлэгч ҮҮСГЭХ, ЗАСАХ нь энд БАЙХГҮЙ — лавлахын хуудсанд
 *    үлдэнэ. Үйл ажиллагааны хуудас, тохиргооны хуудас хоёрыг нэг
 *    дэлгэцэд хольсноос болж ажилтан аль алиныг нь олдоггүй байв.
 *
 * ⚠️ ₽ ба ₮ дүнгүүд `COST_ROLES`-оор хаагдана — борлуулагч авах өртөг
 *    харахгүй.
 *
 * ⚠️ POS-оор бүртгэсэн «Эхний үлдэгдэл» энд ОРОХГҮЙ (2026-09-29) — сервер
 *    шүүнэ (`lib/trip-scope.ts`). Тэдгээр нь «Бүх гутал» → «Эхний
 *    үлдэгдэл» шүүлтүүрт.
 */

import { formatDate, formatMnt, formatQty, formatRub, ulaanbaatarDateStart } from '@boots/domain';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../components/data-table';
import { Section } from '../components/section';
import { StatCard, StatPanel } from '../components/stat-card';
import { Chip, Empty, ErrorNote, Pager, Spinner } from '../components/ui';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  COST_ROLES,
  PURCHASE_STATUS_LABEL,
  type PurchaseListMeta,
  type PurchaseRow,
} from '../lib/types';

/**
 * Нэг хуудсанд хэдэн аялал.
 *
 * ⚠️ 20 байсныг 10 болгов (2026-09-25): хуудас өөрөө гүйдэг байсан
 *    бөгөөд эзэмшигч «хуудастаа тохируулж хэдэн мөр харуулахаа
 *    шийддэг байх» гэж хүссэн. 10 мөр нь KPI, шүүлтүүртэйгээ нийлээд
 *    ердийн дэлгэцэд багтана; цааш нь хуудаслалтаар явна.
 */
const PAGE = 10;

/** Шүүлтүүрийн талбарын нэг хэлбэр — тайлангийн мөртэй ижил. */
const FIELD =
  'min-h-11 rounded-full border border-hair bg-panel px-3.5 py-1 text-base ' +
  'focus:border-accent focus:outline-none sm:min-h-0 sm:text-xs';

/** `/reports/suppliers`-ийн нийлүүлэгчийн мөр — зөвхөн хэрэгтэй талбарууд. */
interface Supplier {
  id: string;
  code: string;
  name: string;
  region: string | null;
  city: string | null;
  isActive: boolean;
  items: number;
  purchases: number;
  lastCollectedAt: string | null;
  creditRub: string | null;
  creditTrips: number;
  /** Тохирсон хугацаа нь хэтэрсэн аялалын тоо. */
  creditOverdue: number;
  /** Хамгийн ойрын төлөх хугацаа. */
  creditNextDueAt: string | null;
}

interface SupplierReport {
  counts: {
    total: number;
    active: number;
    itemsThisMonth: number;
    purchases: number;
    avgLandedMnt: string | null;
    creditRub: string | null;
    creditSuppliers: number;
    creditOverdue: number;
  };
  suppliers: Supplier[];
}

export function PurchasingPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const showCost = can(...COST_ROLES);

  // Шүүлтүүр URL-д — ажилтан нэг нийлүүлэгчийн аяллуудыг линкээр
  // хуваалцаж чадна, буцах товч ч ажиллана.
  const [params, setParams] = useSearchParams();
  const supplierId = params.get('supplierId');
  const collectorId = params.get('collectorId') ?? '';
  const fromKey = params.get('from') ?? '';
  const toKey = params.get('to') ?? '';

  const [cursor, setCursor] = useState<string | null>(null);
  const [stack, setStack] = useState<string[]>([]);

  const report = useQuery({
    queryKey: ['suppliers-report'],
    queryFn: () => api<Envelope<SupplierReport>>('/reports/suppliers'),
  });

  /*
    Огноо нь «YYYY-MM-DD» — УБ-ийн тэр өдрийн 00:00-оос дараагийн
    өдрийн 00:00 хүртэл. Хөрвүүлэлтийг `@boots/domain` хийнэ: сервер
    бүтэн агшин хүлээж авдаг, цагийн бүсийг клиент шийднэ.
  */
  const fromAt = fromKey ? ulaanbaatarDateStart(fromKey) : null;
  const toStart = toKey ? ulaanbaatarDateStart(toKey) : null;
  const toAt = toStart ? new Date(toStart.getTime() + 86_400_000 - 1) : null;

  const q = new URLSearchParams({ limit: String(PAGE) });
  if (supplierId) q.set('supplierId', supplierId);
  if (collectorId) q.set('collectorId', collectorId);
  if (fromAt) q.set('from', fromAt.toISOString());
  if (toAt) q.set('to', toAt.toISOString());
  if (cursor) q.set('cursor', cursor);

  const trips = useQuery({
    queryKey: ['purchases', q.toString()],
    queryFn: () =>
      api<Envelope<PurchaseRow[]> & { meta: PurchaseListMeta & { nextCursor: string | null } }>(
        `/purchases?${q.toString()}`,
      ),
    placeholderData: keepPreviousData,
  });

  // Цуглуулагчид — аялал явдаг ажилтнууд
  const staff = useQuery({
    queryKey: ['purchase-collectors'],
    queryFn: () => api<Envelope<{ id: string; fullName: string }[]>>('/purchases/collectors'),
    staleTime: 5 * 60_000,
  });

  /** Шүүлтүүр солих — бусдыг нь ХАДГАЛНА, хуудаслалт эхэлнэ. */
  const patch = (kv: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(kv)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setCursor(null);
    setStack([]);
    setParams(next, { replace: true });
  };

  if (report.isPending) return <Spinner />;
  if (report.error) return <ErrorNote>{(report.error as Error).message}</ErrorNote>;

  const { counts, suppliers } = report.data.data;
  // Нийлүүлээгүй нийлүүлэгч энэ хуудсанд хэрэггүй — бүртгэл нь хоосон.
  // Тэднийг «Тохиргоо → Нийлүүлэгч» дээр бүрэн жагсаана.
  const delivered = suppliers.filter((s) => s.items > 0).sort((a, b) => b.items - a.items);
  const active = delivered.find((s) => s.id === supplierId) ?? null;
  const rows = trips.data?.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      {/* ─────────── KPI ─────────── */}
      <StatPanel>
        <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-3">
          <StatCard
            label="Энэ сард ирсэн"
            icon="truck"
            value={`${formatQty(counts.itemsThisMonth)} ш`}
            delta={{ text: `${formatQty(counts.purchases)} аялал бүртгэлтэй`, good: true }}
          />
          <StatCard
            label="Нийлүүлсэн нийлүүлэгч"
            icon="users"
            value={formatQty(delivered.length)}
            delta={{ text: `Нийт ${formatQty(counts.total)} бүртгэлтэй` }}
          />
          {/*
            Нийлүүлэгчид өгөх өр. Гутлын мөрүүдээс (`items.is_credit`)
            нийлбэрлэгдэнэ — аялалын дэлгэрэнгүй дэх тоотой ИЖИЛ эх
            сурвалж, хоёр газар хоёр өөр дүн гарахгүй.
          */}
          <StatCard
            label="Нийлүүлэгчид өгөх өр"
            icon="coins"
            value={showCost ? formatRub(counts.creditRub) : '•••'}
            delta={{
              text:
                counts.creditOverdue > 0
                  ? `${formatQty(counts.creditOverdue)} аялалын хугацаа хэтэрсэн`
                  : `${formatQty(counts.creditSuppliers)} нийлүүлэгч`,
              good: counts.creditOverdue > 0 ? false : undefined,
            }}
            pending={!showCost}
          />
        </div>
      </StatPanel>

      <Section
        title="Аялал"
        subtitle={active ? active.name : 'Бүх нийлүүлэгч'}
        note={
          active
            ? `${formatQty(active.items)} ш · ${formatQty(active.purchases)} аялал${
                active.lastCollectedAt ? ` · сүүлд ${formatDate(active.lastCollectedAt)}` : ''
              }`
            : 'Шинэ нь дээрээ · мөр дээр дарж дэлгэрэнгүйг үзнэ'
        }
      >
        {/*
          ⚠️ ЧИП БИШ DROPDOWN (2026-09-25). Нийлүүлэгч 46 болсон тул
             чипүүд хэдэн мөр эзэлж, хуудсыг уртасгаж байв. Шүүлтүүр
             бүгд нэг эгнээнд: нийлүүлэгч · ажилтан · огноо.
        */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Нийлүүлэгч"
            value={supplierId ?? ''}
            onChange={(e) => patch({ supplierId: e.target.value || null })}
            className={`${FIELD} max-w-[230px] ${
              supplierId ? 'border-accent-700 bg-accent-100 text-accent-700' : 'text-ink-2'
            }`}
          >
            <option value="">Бүх нийлүүлэгч</option>
            {delivered.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {formatQty(s.items)} ш
              </option>
            ))}
          </select>

          <select
            aria-label="Аялал явсан ажилтан"
            value={collectorId}
            onChange={(e) => patch({ collectorId: e.target.value || null })}
            className={`${FIELD} max-w-[200px] ${
              collectorId ? 'border-accent-700 bg-accent-100 text-accent-700' : 'text-ink-2'
            }`}
          >
            <option value="">Бүх ажилтан</option>
            {(staff.data?.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.fullName}</option>
            ))}
          </select>

          <span className="flex items-center gap-1.5">
            <input
              type="date"
              aria-label="Эхлэх огноо"
              value={fromKey}
              max={toKey || undefined}
              onChange={(e) => patch({ from: e.target.value || null })}
              className={`${FIELD} ${fromKey ? 'border-accent-700 text-accent-700' : 'text-ink-2'}`}
            />
            <span aria-hidden className="text-ink-3">–</span>
            <input
              type="date"
              aria-label="Дуусах огноо"
              value={toKey}
              min={fromKey || undefined}
              onChange={(e) => patch({ to: e.target.value || null })}
              className={`${FIELD} ${toKey ? 'border-accent-700 text-accent-700' : 'text-ink-2'}`}
            />
          </span>

          {supplierId || collectorId || fromKey || toKey ? (
            <Chip
              onClick={() =>
                patch({ supplierId: null, collectorId: null, from: null, to: null })
              }
            >
              Цэвэрлэх
            </Chip>
          ) : null}

          <span className="num ml-auto text-[12.5px] whitespace-nowrap text-ink-2">
            {trips.data ? `${formatQty(trips.data.meta.total)} аялал` : '…'}
          </span>
          {/* POS-оор бүртгэсэн эхний үлдэгдэл аялал биш — тусдаа жагсаалт */}
          <Link
            to="/items?origin=opening&status=all"
            className="text-[12.5px] whitespace-nowrap text-accent-700 hover:underline"
          >
            Эхний үлдэгдэл →
          </Link>
        </div>

        {/*
          Өртэй нийлүүлэгчид — нэг харцаар. Шүүлтүүр сонгосон бол
          зөвхөн тэр нэг, эс бөгөөс өртэй бүгд.

          ⚠️ Хугацаа хэтэрсэн нь ЯГААНААР: цуглуулагч аялал дуусгахдаа
             тохирсон өдрөө заасан бөгөөд тэр өдөр өнгөрсөн гэсэн үг.
        */}
        {showCost ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {(active ? [active] : delivered)
              .filter((s) => s.creditTrips > 0)
              .map((s) => {
                const late = s.creditOverdue > 0;
                return (
                  <span
                    key={s.id}
                    className={`text-meta ${late ? 'text-alert-ink' : 'text-ink-2'}`}
                  >
                    {active ? '' : `${s.name} · `}
                    <span className="num">{formatRub(s.creditRub)}</span> өртэй
                    {s.creditNextDueAt ? (
                      <>
                        {' · '}
                        {late ? 'хугацаа хэтэрсэн ' : 'төлөх '}
                        <span className="num">{formatDate(s.creditNextDueAt)}</span>
                      </>
                    ) : (
                      ' · хугацаа заагаагүй'
                    )}
                  </span>
                );
              })}
          </div>
        ) : null}

        {trips.error ? <ErrorNote>{(trips.error as Error).message}</ErrorNote> : null}

        <DataTable
          rows={rows}
          columns={tripColumns(showCost)}
          rowKey={(p) => p.id}
          onRowClick={(p) => navigate(`/purchasing/${p.id}`)}
          loading={trips.isPending}
          empty={<Empty>Бүртгэгдсэн аялал алга</Empty>}
        />

        <Pager
          page={stack.length + 1}
          canPrev={stack.length > 0 && !trips.isFetching}
          canNext={Boolean(trips.data?.meta.nextCursor) && !trips.isFetching}
          onPrev={() => {
            const prev = [...stack];
            prev.pop();
            setStack(prev);
            setCursor(prev.at(-1) ?? null);
          }}
          onNext={() => {
            const next = trips.data?.meta.nextCursor;
            if (!next) return;
            setStack([...stack, next]);
            setCursor(next);
          }}
        />
      </Section>
    </div>
  );
}

/**
 * Аялалын мөр — «хэзээ · хэн · хэдээр · хэдэн төгрөг».
 *
 * ⚠️ `itemsRegistered` нь БОДИТООР бүртгэгдсэн мөрийн тоо,
 *    `itemCount` (цуглуулагчийн заасан) БИШ. Хоёрын зөрүү нь
 *    дэлгэрэнгүй хуудасны тулгалтад харагдана.
 */
function tripColumns(showCost: boolean): Column<PurchaseRow>[] {
  const cols: Column<PurchaseRow>[] = [
    {
      key: 'purchasedAt',
      header: 'Огноо',
      nowrap: true,
      width: 'w-[110px]',
      cell: (p) => <span className="num">{formatDate(p.purchasedAt)}</span>,
    },
    {
      key: 'code',
      header: 'Дугаар',
      nowrap: true,
      mobile: 'title',
      cell: (p) => <span className="num">{p.code ?? '—'}</span>,
    },
    {
      key: 'supplier',
      header: 'Нийлүүлэгч',
      cell: (p) => <span className="text-pretty">{p.supplier.name}</span>,
    },
    {
      key: 'collector',
      header: 'Цуглуулагч',
      cell: (p) => <span className="text-ink-2 text-pretty">{p.collector?.fullName ?? '—'}</span>,
    },
    {
      key: 'items',
      header: 'Ширхэг',
      align: 'right',
      mobile: 'value',
      cell: (p) => `${formatQty(p.itemsRegistered)} ш`,
    },
  ];

  if (showCost) {
    cols.push(
      {
        key: 'rub',
        header: 'Эх дүн',
        align: 'right',
        nowrap: true,
        cell: (p) => formatRub(p.goodsAmount),
      },
      {
        key: 'mnt',
        header: 'Нийт өртөг',
        align: 'right',
        nowrap: true,
        cell: (p) => formatMnt(p.landedCostMnt),
      },
    );
  }

  cols.push({
    key: 'status',
    header: 'Төлөв',
    nowrap: true,
    cell: (p) => (
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="rounded-full bg-hair/60 px-2.5 py-[3px] text-label text-ink-2">
          {PURCHASE_STATUS_LABEL[p.status]}
        </span>
        {p.closedAt ? (
          <span className="rounded-full bg-accent-100 px-2.5 py-[3px] text-label text-accent-700">
            Дууссан
          </span>
        ) : null}
      </span>
    ),
  });

  return cols;
}
